import {
  TimelineEntry,
  TimelinePartyType,
  TimelineStatus,
  TransferParty,
} from '~/app/pages/Transfers/_components/Timeline/types';
import { buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { AdminTransfer } from '~/repositories';
import { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';

const adminEvents = [
  {
    label: 'Created',
    field: 'createdAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Completed',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
];

export const buildAdminEvents = ({
  transfer,
  bankAccount,
  accountNumber,
  entityType,
}: {
  transfer: AdminTransfer;
  bankAccount: BankAccount;
  accountNumber?: AccountNumber;
  entityType?: 'person' | 'business';
}): TimelineEntry[] => {
  // A `credit` transfer credits the customer account (Column sends), a `debit` debits it (Column receives).
  const isCredit = transfer.type === 'credit';

  const customerParty = (partyType: TimelinePartyType): TransferParty | undefined =>
    accountNumber
      ? buildTransferParty({
          account: {
            accountNumber,
            bankAccount,
            entityType,
            isBookTransfer: false,
          },
          isIncoming: isCredit,
          isOriginator: partyType === TimelinePartyType.Sender,
        })
      : undefined;

  // Admin transfers move funds between Column-internal accounts, so Column is always the other side.
  const columnParty = (partyType: TimelinePartyType): TransferParty => ({
    data: { adminName: 'Column National Association' },
    partyType,
  });

  const entries: TimelineEntry[] = [];

  adminEvents
    .filter((event) =>
      event.field === 'completedAt' ? !!transfer.completedAt || !!transfer.createdAt : !!transfer.createdAt
    )
    .forEach((event) => {
      const isCreatedEvent = event.field === 'createdAt';

      // "Created" shows the party losing funds, "Completed" the party receiving them.
      const partyData = isCreatedEvent
        ? isCredit
          ? columnParty(TimelinePartyType.Sender)
          : customerParty(TimelinePartyType.Sender)
        : isCredit
          ? customerParty(TimelinePartyType.Receiver)
          : columnParty(TimelinePartyType.Receiver);

      const amount = transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: !isCreatedEvent,
            currencyCode: transfer.currency || 'USD',
          }
        : undefined;

      const dateValue = isCreatedEvent ? transfer.createdAt : transfer.completedAt || transfer.createdAt;

      entries.push({
        label: event.label,
        amount,
        date: dateValue ? new Date(dateValue) : undefined,
        partyData,
        status: event.status,
      });
    });

  return entries;
};
